/**
 * Semantic design tokens - what components consume.
 * Two sets: light and dark. Components read `theme.tokens.xxx`.
 */

import { primitives } from './primitives';

export const lightTokens = {
  bg: primitives.neutral[25],
  surface: primitives.neutral[0],
  surfaceSubtle: primitives.neutral[50],
  surfaceMuted: primitives.neutral[100],

  border: '#E6E8EC',
  borderStrong: '#CDD1D8',
  borderSubtle: '#F0F1F4',

  fgPrimary: primitives.neutral[900],
  fgSecondary: primitives.neutral[600],
  fgTertiary: primitives.neutral[400],
  fgOnAccent: primitives.neutral[0],

  accent: primitives.clarivatePurple[500],
  accentHover: primitives.clarivatePurple[600],
  accentSubtle: primitives.clarivatePurple[50],
  accentBorder: primitives.clarivatePurple[200],
  accentText: primitives.clarivatePurple[600],
  accentTextStrong: primitives.clarivatePurple[700],

  successBg: 'rgba(22,163,74,0.08)',
  successFg: '#16A34A',
  successBorder: 'rgba(22,163,74,0.32)',
  warningBg: 'rgba(229,165,10,0.10)',
  warningFg: '#B45309',
  warningBorder: 'rgba(229,165,10,0.32)',
  dangerBg: 'rgba(220,38,38,0.08)',
  dangerFg: '#DC2626',
  dangerBorder: 'rgba(220,38,38,0.32)',
  infoBg: 'rgba(37,99,235,0.08)',
  infoFg: '#2563EB',
  infoBorder: 'rgba(37,99,235,0.32)',

  sidebarBg: primitives.neutral[0],
  sidebarBorder: '#EAECF0',
  sidebarItemHover: primitives.neutral[50],
  sidebarItemActive: primitives.clarivatePurple[50],
  sidebarItemActiveFg: primitives.clarivatePurple[700],
  sidebarItemActiveBar: primitives.clarivatePurple[500],
  sidebarSectionLabel: primitives.neutral[400],

  topbarBg: 'rgba(255,255,255,0.72)',
  topbarBorder: '#EAECF0',

  footerBg: primitives.neutral[25],
  footerFg: primitives.neutral[500],

  success: primitives.semantic.success,
  warning: primitives.semantic.warning,
  danger: primitives.semantic.danger,
  info: primitives.semantic.info,

  tableHeaderBg: primitives.neutral[50],
  tableHeaderFg: primitives.neutral[600],
  tableHeaderFgActive: primitives.neutral[900],
  tableHeaderBorder: '#E6E8EC',
  tableRowBg: primitives.neutral[0],
  tableRowBgHover: primitives.neutral[50],
  tableRowBgSelected: primitives.clarivatePurple[50],
  tableRowBorder: '#F0F1F4',
  tableCellPadX: 12,
  tableCellPadY: 8,
};

export type SemanticTokens = {
  [K in keyof typeof lightTokens]: (typeof lightTokens)[K] extends number ? number : string;
};

export const darkTokens: SemanticTokens = {
  bg: primitives.neutral[950],
  surface: primitives.neutral[900],
  surfaceSubtle: primitives.neutral[800],
  surfaceMuted: primitives.neutral[700],

  border: '#1F232C',
  borderStrong: '#2A2F3A',
  borderSubtle: '#15181E',

  fgPrimary: '#E5E7EB',
  fgSecondary: '#9CA3AF',
  fgTertiary: '#6B7280',
  fgOnAccent: '#FFFFFF',

  accent: primitives.clarivatePurple[300],
  accentHover: primitives.clarivatePurple[200],
  accentSubtle: 'rgba(177,117,225,0.16)',
  accentBorder: 'rgba(177,117,225,0.40)',
  accentText: primitives.clarivatePurple[200],
  accentTextStrong: primitives.clarivatePurple[100],

  successBg: 'rgba(52,211,153,0.12)',
  successFg: '#34D399',
  successBorder: 'rgba(52,211,153,0.40)',
  warningBg: 'rgba(251,191,36,0.12)',
  warningFg: '#FBBF24',
  warningBorder: 'rgba(251,191,36,0.40)',
  dangerBg: 'rgba(248,113,113,0.12)',
  dangerFg: '#F87171',
  dangerBorder: 'rgba(248,113,113,0.40)',
  infoBg: 'rgba(96,165,250,0.12)',
  infoFg: '#60A5FA',
  infoBorder: 'rgba(96,165,250,0.40)',

  sidebarBg: '#0B0D12',
  sidebarBorder: '#15181E',
  sidebarItemHover: '#15181E',
  sidebarItemActive: 'rgba(122,70,210,0.12)',
  sidebarItemActiveFg: primitives.clarivatePurple[200],
  sidebarItemActiveBar: primitives.clarivatePurple[400],
  sidebarSectionLabel: '#6B7280',

  topbarBg: 'rgba(11,13,18,0.72)',
  topbarBorder: '#15181E',

  footerBg: '#0B0D12',
  footerFg: '#6B7280',

  success: '#34D399',
  warning: '#FBBF24',
  danger: '#F87171',
  info: '#60A5FA',

  tableHeaderBg: '#111318',
  tableHeaderFg: '#9CA3AF',
  tableHeaderFgActive: '#E5E7EB',
  tableHeaderBorder: '#1F232C',
  tableRowBg: primitives.neutral[900],
  tableRowBgHover: primitives.neutral[800],
  tableRowBgSelected: 'rgba(122,70,210,0.12)',
  tableRowBorder: '#15181E',
  tableCellPadX: 12,
  tableCellPadY: 8,
};
